import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import "./multiRangeSlider.scss";

const MultiRangeSlider = ({
  min,
  max,
  minValue,
  maxValue,
  onChange
}) => {
  const [minVal, setMinVal] = useState(minValue ?? min);
  const [maxVal, setMaxVal] = useState(maxValue ?? max);

  const range = useRef(null);

  // ✅ Sync ONLY when parent resets
  useEffect(() => {
    setMinVal(minValue ?? min);
    setMaxVal(maxValue ?? max);
  }, [minValue, maxValue, min, max]);

  const getPercent = useCallback(
    (value) => ((value - min) / (max - min)) * 100,
    [min, max]
  );

  // ✅ Update bar visually (NO onChange here)
  useEffect(() => {
    if (!range.current) return;

    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxVal);

    range.current.style.left = `${minPercent}%`;
    range.current.style.width = `${maxPercent - minPercent}%`;
  }, [minVal, maxVal, getPercent]);

  return (
    <div className="multiRangeSlider__wrap">
      {/* MIN */}
      <input
        type="range"
        min={min}
        max={max}
        value={minVal}
        onChange={(e) => {
          const value = Math.min(+e.target.value, maxVal - 1);
          setMinVal(value);
          onChange({ min: value, max: maxVal }); // ✅ USER ACTION ONLY
        }}
        className="thumb thumb--left"
      />

      {/* MAX */}
      <input
        type="range"
        min={min}
        max={max}
        value={maxVal}
        onChange={(e) => {
          const value = Math.max(+e.target.value, minVal + 1);
          setMaxVal(value);
          onChange({ min: minVal, max: value }); // ✅ USER ACTION ONLY
        }}
        className="thumb thumb--right"
      />

      <div className="multiRangeSlider__slider">
        <div className="slider__track" />
        <div ref={range} className="slider__range" />
        <div className="slider__left-value">{minVal}</div>
        <div className="slider__right-value">{maxVal}</div>
      </div>
    </div>
  );
};

MultiRangeSlider.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  minValue: PropTypes.number,
  maxValue: PropTypes.number,
  onChange: PropTypes.func.isRequired
};

export default MultiRangeSlider;
